import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Row, Col, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
// import { fetchOrgDropDown } from 'slices/UserManagement/department/thunk'
import { Axios } from "api/axios";
import { Autocomplete } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { fetchOrganizationDropdown } from "slices/UserManagement/organisation/thunk";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import {
  addDepartment,
  fetchDeptById,
  updateDepartment,
} from "slices/UserManagement/department/thunk";
import {
  autocompleteDropdownStyles,
  getDisabledInputStyles,
} from "Constants/Style";
import LoadingSpinner from "components/LoadingSpinner";
import BootstrapTextField from "Common/BootstrapTextField";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";

const DepartmentForm = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [loading, setLoading] = useState<Boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchOrganizationDropdown());
  }, [dispatch]);

  const orgDropDown: any[] = useSelector(
    (state: any) => state.Organisations.orgDropDown,
  );
  const orgDropDownLoading = useSelector(
    (state: any) => state.Organisations.loading,
  );

  const formik = useFormik({
    initialValues: {
      organization: "",
      department_name: "",
      organization_name: "",
      department_id: "",
    },
    validationSchema: Yup.object({
      organization: Yup.string().required("Organisation Name is required"),
      department_name: Yup.string()
        .trim()
        .required("Department Name is required")
        .min(3, "Department Name must be at least 3 characters"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const payload = {
          organization_id: values.organization,
          department_name: values.department_name.trim(),
        };

        let response;
        if (id) {
          // response = await Axios.patch(`/department/${id}/update/`, payload)
          response = await dispatch(
            updateDepartment({
              id: id,
              depData: payload,
            }),
          ).unwrap();
        } else {
          response = await dispatch(addDepartment(payload)).unwrap();
          clearTableState(TableKeys?.USER_MANAGEMENT?.DEPARTMENT);
        }
        toast.success(`Department ${id ? "updated" : "created"} successfully`);
        navigate("/department-list");
      } catch (error: any) {
        toast.error(error?.error || error?.message || "Something went wrong");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useScrollToFormikError(
    formik.errors,
    formik.submitCount,
    formik.isSubmitting,
  );

  useEffect(() => {
    setLoading(true);
    if (!id) {
      return;
    }
    const fetchDept = async () => {
      try {
        const res = await dispatch(fetchDeptById(id)).unwrap();
        const data = res?.data?.data;
        formik.setValues({
          organization: data?.organization_id || "",
          department_name: data?.department_name || "",
          organization_name: data?.organization?.organization_name || "",
          department_id: id,
        });
      } catch (error: any) {
        toast.error(error?.message || "Failed to load Department");
      } finally {
        setLoading(false);
      }
    };
    fetchDept();
  }, [id, isEdit]);

  return (
    <PageContainer>
      <PageBreadcrumb
        title={id ? "Edit Department" : "Add Department"}
        titleLink="/department-list"
        breadcrumbs={[
          { label: "Department", to: "/department-list" },
          { label: id ? "Edit Department" : "Add Department", to: "" },
        ]}
        backNavigationPath={-1}
      />
      <hr />
      {isEdit && loading ? (
        <LoadingSpinner />
      ) : (
        <Form noValidate onSubmit={formik.handleSubmit}>
          <Row className="mb-4">
            {/* Organisation */}
            <Col md={6} xs={12} className="mb-3 mb-md-0">
              <Form.Label className="fw-semibold">
                Organisation Name <span className="text-danger">*</span>
              </Form.Label>
              {orgDropDownLoading ? (
                <div className="d-flex align-items-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading...
                </div>
              ) : (
                <Autocomplete
                  disablePortal
                  options={orgDropDown}
                  getOptionLabel={(option: any) =>
                    option.organization_name || ""
                  }
                  value={
                    orgDropDown.find(
                      (org) =>
                        org?.organization_id === formik.values.organization,
                    ) || null
                  }
                  onChange={(_, value) => {
                    formik.setFieldValue(
                      "organization",
                      value?.organization_id || "",
                    );
                    formik.setFieldValue(
                      "organization_name",
                      value?.organization_name || "",
                    );
                    formik.validateField("organization"); // validate after change
                  }}
                  onBlur={() => formik.setFieldTouched("organization", true)} // touched on blur only
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt.organization_name,
                    )
                  }
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Organisation"
                      id="organization"
                      name="organization"
                      error={
                        formik.touched.organization &&
                        !!formik.errors.organization
                      }
                      helperText={
                        formik.touched.organization &&
                        formik.errors.organization
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        // ...getDisabledInputStyles(),
                      }}
                      fullWidth
                      size="small"
                      autoComplete="off"
                    />
                  )}
                />
              )}
            </Col>

            {/* Department Name */}
            <Col md={6} xs={12}>
              <Form.Label className="fw-semibold">
                Department Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                id="department_name"
                name="department_name"
                placeholder="Enter Department Name"
                value={formik.values.department_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  formik.touched.department_name &&
                  !!formik.errors.department_name
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.department_name}
              </Form.Control.Feedback>
            </Col>
          </Row>

          {/* Buttons */}
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
            <CancelButton
              redirectTo="/department-list"
              disabled={isSubmitting}
            />
            <PrimarySubmitButton
              label={id ? "Update" : "Submit"}
              isLoading={isSubmitting}
              loadingLabel={id ? "Updating..." : "Submitting..."}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default DepartmentForm;
